import { Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { OrderQueries } from 'src/app/models/shared/enums/get-query';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { Selectable } from 'src/app/shared/models/types/selectable.type';
import { Childable } from 'src/app/shared/models/types/childable.type';
import { AddProductModalComponent } from '../../new-order/add-product-modal/add-product-modal.component';
import { CompanyStyle } from '../../../models/company-style.type';
import { StyleColorShot } from '../../../models/types/style-color-shot.type';
import { CompanyOrderDetails } from '../../../models/types/company-order-details.type';
import { CompanyOrderInfo } from '../../../models/types/company-order-info.type';
import { StyleShot } from '../../../models/types/style-shot.type';
import { ShotGroup } from '../../../models/types/shot-group.type';
import { ImageService } from 'src/app/services/image.service';
import { MutationQuery } from 'src/app/models/shared/enums/mutation-query.enum';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ApproveOrderNoteComponent } from '../../order-info/approve-order-note/approve-order-note';
import { UnifiedOrderOutletEvent } from '../unified-order-view.component';

@Component({
	selector: 'bt-unified-order-detail-shots',
	standalone: false,
	templateUrl: './unified-order-detail-shots.component.html',
	styleUrls: ['./unified-order-detail-shots.component.scss']
})
export class UnifiedOrderDetailShotsComponent implements OnInit {
	@Output() outletEvent = new EventEmitter<UnifiedOrderOutletEvent>();

	readonly #destroyRef = inject(DestroyRef);
	readonly #graphQLService = inject(GraphQLService);
	readonly #appService = inject(AppService);
	readonly #route = inject(ActivatedRoute);
	readonly #imageService = inject(ImageService);
	readonly #utilitiesService = inject(UtilitiesService);
	readonly #dialog = inject(MatDialog);

	orderIdParam: string | number | null = null;
	isCreateMode = false;
	orderId: number | null = null;
	orderStatusId: number | null = null;

	get isEditMode(): boolean {
		return this.orderStatusId === 1;
	}

	// Create mode
	warehouse: any = null;
	styleColors: Array<Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>> = [];

	// View mode
	products: Array<Selectable<Childable<CompanyOrderDetails, Selectable<ShotGroup>>>> = [];

	get allSelected(): boolean {
		if (this.isCreateMode) {
			return this.styleColors.every((d) => d.selected);
		}
		return this.products.every((detail) => detail.selected);
	}

	get indeterminate(): boolean {
		if (this.isCreateMode) {
			return this.styleColors.some((d) => d.selected) && !this.allSelected;
		}
		return this.products.some((detail) => detail.selected) && !this.allSelected;
	}

	ngOnInit(): void {
		const raw = this.#route.parent?.snapshot?.params['orderId'];
		this.orderIdParam = raw ?? null;
		this.isCreateMode = this.orderIdParam === 'new';
		if (!this.isCreateMode && raw && !isNaN(Number(raw))) {
			this.orderId = Number(raw);
			this.getCompanyOrderDetails();
			// this.getCompanyOrderInfo(Number(raw));
		}
		this.#route.parent?.params?.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((params) => {
			const id = params['orderId'] ?? null;
			this.orderIdParam = id;
			this.isCreateMode = id === 'new';
			if (!this.isCreateMode && id && !isNaN(Number(id))) {
				this.orderId = Number(id);
				this.getCompanyOrderDetails();
				// this.getCompanyOrderInfo(Number(id));
			} else {
				this.orderStatusId = null;
			}
		});
	}

	// ----- Create mode -----
	toggleDetail(detail: Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>): void {
		detail.disabled = !detail.disabled;
	}

	toggleAll(): void {
		if (this.isCreateMode) {
			if (this.allSelected) this.styleColors.forEach((d) => (d.selected = false));
			else this.styleColors.forEach((d) => (d.selected = true));
		} else {
			if (this.allSelected) {
				this.products.forEach((detail) => {
					detail.selected = false;
					detail.children.forEach((group) => (group.selected = false));
				});
			} else {
				this.products.forEach((detail) => {
					detail.selected = true;
					detail.children.forEach((group) => (group.selected = true));
				});
			}
		}
	}

	selectProduct(detail: Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>, ev: Event): void {
		ev.stopPropagation();
		const ind = this.isIndeterminateCreate(detail);
		detail.selected = ind ? true : false;
		detail.children.forEach((child) => (child.selected = detail.selected));
	}

	isIndeterminateCreate(detail: Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>): boolean {
		const selectedChildren = detail.children.filter((child) => child.selected);
		return selectedChildren.length > 0 && selectedChildren.length < detail.children.length;
	}

	removeSelected(): void {
		const sel = this.getSelectedIds();
		if (this.isCreateMode && 'byModel' in sel) {
			const colorSelected = sel.byModel;
			const models = Object.keys(colorSelected);
			this.styleColors = this.styleColors.filter((detail) => {
				const hasSelectedChildren = detail.children.some((c) => c.selected);
				if (hasSelectedChildren && models.includes(detail.modelId.toString())) {
					detail.children.forEach((color) => {
						const match = colorSelected[detail.modelId]?.find(
							(el) => el.colorId === color.colorId && (el.sizeGroupId === color.sizeGroupId || (el.sizeGroupId == null && color.sizeGroupId == null))
						);
						if (match) {
							color.shots = [];
						}
					});
					detail.children = detail.children.filter((color) => color.shots.length > 0);
				}
				return !hasSelectedChildren || detail.children.length > 0;
			});
		} else if ('shotIds' in sel) {
			const shotIdsSet = new Set(sel.shotIds);
			if (shotIdsSet.size === 0) {
				this.#utilitiesService.showSnackbar('Select at least one item to remove');
				return;
			}
			this.products = this.products
				.map((detail) => ({
					...detail,
					children: detail.children
						.map((group) => ({
							...group,
							shots: group.shots.filter((s) => !shotIdsSet.has(s.shotId))
						}))
						.filter((group) => group.shots.length > 0)
				}))
				.filter((detail) => detail.children.length > 0);
		}
	}

	addProductModal(): void {
		const dialogRef = this.#dialog.open(AddProductModalComponent, {
			autoFocus: false,
			data: { warehouse: this.warehouse ?? null }
		});
		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe((result) => {
				if (!result) return;
				this.warehouse = this.warehouse ?? result.warehouse;
				const style: Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>> = {
					...result.style,
					selected: false,
					disabled: false,
					displayId: result.modelId,
					displayName: result.style.fullName,
					children: result.colors.map((color) => ({ ...color }))
				};
				const colorIds = result.colors.map((color) => color.colorId);
				if (this.isCreateMode) {
					this.getShots(style.modelId, colorIds, style.brandId, style.categoryId, style);
					const existingStyle = this.styleColors.find((el) => el.modelId === style.modelId);
					if (existingStyle) {
						existingStyle.children = existingStyle.children.concat(style.children);
					} else {
						this.styleColors.push(style);
					}
				} else {
					this.getShots(style.modelId, colorIds, style.brandId, style.categoryId, style, (filledStyle) => {
						const detail = this.buildDetailFromStyle(filledStyle);
						const existing = this.products.find((d) => d.modelId === detail.modelId);
						if (existing) {
							existing.children = existing.children.concat(detail.children);
						} else {
							this.products.push(detail);
						}
					});
				}
			});
	}

	/** Converts modal style (with shots from GET_SHOTS) into one product detail for the view list. */
	private buildDetailFromStyle(
		style: Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>
	): Selectable<Childable<CompanyOrderDetails, Selectable<ShotGroup>>> {
		const tempStyleOrderId = -(Math.abs(Date.now()) % 1e6);
		const orderId = this.orderId ?? 0;
		const children: Array<Selectable<ShotGroup>> = style.children.map((child, groupIndex) => ({
			colorId: child.colorId,
			colorName: child.colorName,
			sizeGroupId: child.sizeGroupId,
			sizeGroupName: child.sizeGroupName,
			shots: (child.shots ?? []).map((s: any, shotIndex) =>
				this.toStyleShot(s, orderId, tempStyleOrderId, groupIndex, shotIndex)
			),
			displayId: child.displayId,
			displayName: child.displayName,
			selected: false,
			disabled: false
		}));
		return {
			orderId,
			styleOrderId: tempStyleOrderId,
			modelId: style.modelId,
			styleCode: (style as any).styleCode ?? style.fullName ?? '',
			styleName: style.fullName ?? '',
			selected: false,
			disabled: false,
			displayId: style.displayId,
			displayName: style.displayName,
			children
		};
	}

	private toStyleShot(
		shot: any,
		orderId: number,
		styleOrderId: number,
		groupIndex: number,
		shotIndex: number
	): Selectable<StyleShot> {
		const realShotId = shot.shotId != null && shot.shotId > 0 ? shot.shotId : null;
		const shotId =
			realShotId ??
			-(Math.abs(styleOrderId) * 100000 + groupIndex * 1000 + shotIndex);
		const s: StyleShot = {
			modelId: shot.modelId ?? 0,
			colorId: shot.colorId ?? 0,
			colorName: shot.colorName ?? '',
			sizeGroupId: shot.sizeGroupId,
			sizeGroupName: shot.sizeGroupName ?? '',
			orderId,
			styleOrderId,
			brandId: shot.brandId ?? 0,
			categoryId: shot.categoryId ?? 0,
			templateId: shot.templateId ?? 0,
			shotTypeId: shot.shotTypeId ?? 0,
			shotType: shot.shotType ?? shot.shotTypeName ?? '',
			aspectRatioId: shot.aspectRatioId ?? 0,
			aspectRatio: shot.aspectRatio ?? shot.aspectRatioCode ?? '',
			mediaFormatId: shot.mediaFormatId ?? 0,
			mediaFormat: shot.mediaFormat ?? shot.mediaFormatCode ?? '',
			codifiedUsedId: shot.codifiedUsedId ?? 0,
			codifiedUsed: shot.codifiedUsed ?? shot.codifiedUsedCode ?? '',
			shotId,
			shotApprovalStatusId: shot.shotApprovalStatusId ?? 0,
			imgPath: shot.imgPath ?? ''
		};
		return {
			...s,
			displayId: shot.shotId ?? shot.shotTypeId ?? shotId,
			displayName: shot.shotType ?? shot.shotTypeName ?? '',
			selected: false,
			disabled: false
		};
	}

	private getShots(
		modelId: number,
		colorIds: Array<number>,
		brandId: number,
		categoryId: number,
		style: Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>,
		onDone?: (style: Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>) => void
	): void {
		this.#graphQLService
			.getData<StyleColorShot>(OrderQueries.GET_SHOTS, {
				modelId,
				colorIds,
				brandId,
				categoryId,
				companyId: this.#appService.companyId
			})
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe((res: any) => {
				const shots: any[] = res?.data?.shots ?? [];
				const colorNamesByColorId = new Map<number, string>();
				style.children.forEach((c: any) => colorNamesByColorId.set(c.colorId, c.colorName ?? ''));
				const groupMap = new Map<
					string,
					{ colorId: number; colorName: string; sizeGroupId?: number; sizeGroupName?: string; shots: any[] }
				>();
				shots.forEach((shot: any) => {
					const key = `${shot.colorId ?? 'c'}-${shot.sizeGroupId ?? 'sg'}`;
					if (!groupMap.has(key)) {
						groupMap.set(key, {
							colorId: shot.colorId ?? 0,
							colorName: shot.colorName ?? colorNamesByColorId.get(shot.colorId) ?? '',
							sizeGroupId: shot.sizeGroupId,
							sizeGroupName: shot.sizeGroupName,
							shots: []
						});
					}
					groupMap.get(key)!.shots.push(shot);
				});
				const newChildren: Array<Selectable<StyleColorShot>> = [];
				groupMap.forEach((g) => {
					newChildren.push({
						colorId: g.colorId,
						colorName: g.colorName,
						sizeGroupId: g.sizeGroupId,
						sizeGroupName: g.sizeGroupName,
						defaultColor: false,
						shots: g.shots,
						displayId: g.colorId * 1000 + (g.sizeGroupId ?? 0),
						displayName:
							g.sizeGroupName != null && g.sizeGroupName !== ''
								? `${g.colorName} - ${g.sizeGroupName}`
								: g.colorName,
						selected: false,
						disabled: false
					});
				});
				style.children = newChildren;
				onDone?.(style);
			});
	}

	private getSelectedIds():
		| { byModel: { [key: string]: Array<{ colorId: number; sizeGroupId?: number }> } }
		| { shotIds: number[] } {
		if (this.isCreateMode) {
			const ids: { [key: string]: Array<{ colorId: number; sizeGroupId?: number }> } = {};
			this.styleColors.forEach((detail) => {
				detail.children.forEach((child) => {
					if (child.selected) {
						if (!ids[detail.modelId]) ids[detail.modelId] = [];
						ids[detail.modelId].push({ colorId: child.colorId, sizeGroupId: child.sizeGroupId });
					}
				});
			});
			return { byModel: ids };
		}
		const shotIds: number[] = [];
		this.products.forEach((detail) => {
			if (detail.selected) {
				detail.children.forEach((group) => {
					group.shots.forEach((shot) => shotIds.push(shot.shotId));
				});
			} else {
				detail.children.forEach((group) => {
					if (group.selected) {
						group.shots.forEach((shot) => shotIds.push(shot.shotId));
					}
				});
			}
		});
		return { shotIds };
	}

	confirmOrderDetails(): void {
		if (this.isCreateMode) {
			this.outletEvent.emit({
				type: 'savedOrderDetails',
				payload: { styleColors: this.styleColors, warehouseId: this.warehouse?.id }
			});
		} else {
			this.outletEvent.emit({
				type: 'savedOrderDetailsEdit',
				payload: { products: this.products, orderId: this.orderId, warehouseId: this.warehouse?.id }
			});
		}
	}

	// ----- View mode -----
	toggleDetailView(detail: Selectable<CompanyOrderDetails>): void {
		detail.disabled = !detail.disabled;
	}

	onDetailSelectionChange(detail: Selectable<Childable<CompanyOrderDetails, Selectable<ShotGroup>>>): void {
		detail.children.forEach((group) => (group.selected = detail.selected));
	}

	onGroupSelectionChange(detail: Selectable<Childable<CompanyOrderDetails, Selectable<ShotGroup>>>): void {
		const allSelected = detail.children.every((group) => group.selected);
		detail.selected = allSelected;
	}

	isIndeterminateView(detail: Selectable<Childable<CompanyOrderDetails, Selectable<ShotGroup>>>): boolean {
		const selectedCount = detail.children.filter((group) => group.selected).length;
		return selectedCount > 0 && selectedCount < detail.children.length;
	}

	downloadSelected(): void {
		const sel = this.getSelectedIds();
		if ('shotIds' in sel && sel.shotIds.length > 0) {
			this.#imageService
				.downloadImagesByShotIds(sel.shotIds)
				.pipe(takeUntilDestroyed(this.#destroyRef))
				.subscribe({
					next: (res: Blob) => {
						const url = window.URL.createObjectURL(res);
						window.open(url, '_blank');
					},
					error: (err) => console.error(err)
				});
		}
	}

	/** Remove selected products from order (edit mode only). */
	changeColorShotApprovalState(styleOrderId: number, shotApprovalStateId: number): void {
		const detail = this.products.find((el) => el.styleOrderId === styleOrderId);
		const ids: Array<number> = [];
		detail.children.forEach((group) => {
			group.shots.forEach((shot) => {
				if (shot.shotApprovalStatusId !== 3 && shot.shotApprovalStatusId !== 4) {
					ids.push(shot.shotId);
				}
			});
		});
		this.changeShotApprovalState(ids, shotApprovalStateId);
	}

	changeGroupShotApprovalState(group: Selectable<ShotGroup>, shotApprovalStateId: number): void {
		const ids: Array<number> = [];
		group.shots.forEach((shot) => {
			if (shot.shotApprovalStatusId !== 3 && shot.shotApprovalStatusId !== 4) {
				ids.push(shot.shotId);
			}
		});
		if (ids.length > 0) this.changeShotApprovalState(ids, shotApprovalStateId);
	}

	changeSelectedShotApprovalState(shotApprovalStateId: number): void {
		const sel = this.getSelectedIds();
		if ('shotIds' in sel && sel.shotIds.length > 0) {
			this.changeShotApprovalState(sel.shotIds, shotApprovalStateId);
		}
	}

	changeShotApprovalState(shotIds: Array<number>, shotApprovalStateId: number): void {
		const dialog = this.#dialog.open(ApproveOrderNoteComponent);
		dialog.afterClosed().subscribe((result) => {
			if (result) {
				const variables = {
					currentUserId: this.#appService.user.userId,
					shotIds,
					statusId: shotApprovalStateId,
					comments: result,
					companyId: this.#appService.companyId
				};
				this.#graphQLService
					.postData(MutationQuery.UPDATE_SHOT_STATUS, variables)
					.pipe(takeUntilDestroyed(this.#destroyRef))
					.subscribe({
						next: (res) => {
							if ((res.data as any).updateShotStatus.success) {
								this.getCompanyOrderDetails();
								this.#utilitiesService.showSnackbar('Shot approval state changed successfully');
							}
						},
						error: (err) => this.#utilitiesService.showSnackbar(err)
					});
			}
		});
	}

	private groupShotsByColorAndSize(shots: Array<StyleShot>): Array<Selectable<ShotGroup>> {
		const groupMap = new Map<
			string,
			{
				colorId: number;
				colorName: string;
				sizeGroupId: number;
				sizeGroupName: string;
				shots: Array<StyleShot>;
			}
		>();
		for (const shot of shots) {
			const key = `${shot.colorId}-${shot.sizeGroupId}`;
			if (!groupMap.has(key)) {
				groupMap.set(key, {
					colorId: shot.colorId,
					colorName: shot.colorName,
					sizeGroupId: shot.sizeGroupId,
					sizeGroupName: shot.sizeGroupName,
					shots: []
				});
			}
			groupMap.get(key)!.shots.push(shot);
		}
		return Array.from(groupMap.values()).map((g) => ({
			colorId: g.colorId,
			colorName: g.colorName,
			sizeGroupId: g.sizeGroupId,
			sizeGroupName: g.sizeGroupName,
			shots: g.shots.map((s) => ({
				...s,
				displayId: (s as any).displayId ?? s.shotId,
				displayName: (s as any).displayName ?? s.shotType,
				selected: (s as any).selected ?? false,
				disabled: (s as any).disabled ?? false
			})),
			displayId: g.colorId * 1000 + g.sizeGroupId,
			displayName: `${g.colorName} - ${g.sizeGroupName}`,
			selected: false,
			disabled: false
		}));
	}

	private getCompanyOrderDetails(): void {
		if (this.orderId == null) return;
		this.#graphQLService
			.getData(OrderQueries.GET_COMPANY_ORDER_DETAILS, {
				companyId: this.#appService.companyId,
				orderId: this.orderId
			})
			.pipe(
				map((res: any) => res['data']['companyOrderDetails']['nodes'][0]),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe({
				next: (data) => {
					const rawDetails = data.orderDetails ?? [];
					this.products = this.groupOrderDetailsByStyleAndColorSize(rawDetails);

					this.warehouse = {id: data.warehouse.warehouseId, code: data.warehouse.warehouseCode, description: data.warehouse.warehouseDescription, fullName: data.warehouse.warehouseFullName};
					this.orderStatusId = data.orderStatusId;
				}
			});
	}

	/**
	 * Raggruppa i dettagli ordine in macro-gruppi per style (modelId) e sotto-gruppi per color-sizegroup.
	 * Dalla GET arriva un array flat; restituisce products: uno per style, con children = gruppi color-size con i relativi shots.
	 */
	private groupOrderDetailsByStyleAndColorSize(
		orderDetails: Array<{ orderId: number; orderStyleId: number; modelId: number; styleCode: string; styleName: string; sizeGroupId?: number; sizeGroupName?: string; shots?: any[] }>
	): Array<Selectable<Childable<CompanyOrderDetails, Selectable<ShotGroup>>>> {
		const byStyle = new Map<
			string,
			{ orderId: number; orderStyleId: number; modelId: number; styleCode: string; styleName: string; shots: any[] }
		>();
		for (const detail of orderDetails) {
			const key = `${detail.modelId}`;
			if (!byStyle.has(key)) {
				byStyle.set(key, {
					orderId: detail.orderId,
					orderStyleId: detail.orderStyleId,
					modelId: detail.modelId,
					styleCode: detail.styleCode ?? '',
					styleName: detail.styleName ?? '',
					shots: []
				});
			}
			const styleGroup = byStyle.get(key)!;
			(detail.shots ?? []).forEach((s: any) => styleGroup.shots.push(s));
		}
		return Array.from(byStyle.values()).map((style) => ({
			orderId: style.orderId,
			styleOrderId: style.orderStyleId,
			modelId: style.modelId,
			styleCode: style.styleCode,
			styleName: style.styleName,
			selected: false,
			disabled: false,
			displayId: style.orderStyleId,
			displayName: (style.styleCode && style.styleName) ? style.styleCode + ' - ' + style.styleName : style.styleName || style.styleCode || String(style.modelId),
			children: this.groupShotsByColorAndSize(style.shots)
		}));
	}

	isOptionEnable(group: Selectable<ShotGroup>): boolean {
		return !group.shots.some((shot) => !!shot.imgPath);
	}
}
